import { useRecurly } from "@recurly/react-recurly";

import { ISKUDetails } from "../../services/api/offering/types";
import { NOW_TIMEFRAME } from "../../services/api/subscription/constants";
import {
  ISubscription,
  ISubscriptionPreview,
} from "../../services/api/subscription/types";
import { ICurrentSubscriptionDetails } from "../../store/subscriptions/types";
import { IMoney } from "../../services/api/subscription/types";
import { IPriceTier } from "../../pages/Subscriptions/components/ManageLicensesModal/types";
import { LicenseType } from "../../types/license";

export const generateAddons = (
  targetLicenseQuantity: number,
  currentSKUAddon: ISKUDetails,
) => {
  if (targetLicenseQuantity === 0) {
    return [];
  }

  return [
    {
      sku_id: currentSKUAddon.sku_id,
      quantity: targetLicenseQuantity,
    },
  ];
};

export const getAddonType = (skuCode: string): LicenseType => {
  if (
    skuCode.endsWith("server-license") ||
    skuCode.endsWith("VPN-SERVER-LICENSE")
  ) {
    return LicenseType.Server;
  }
  return LicenseType.User;
};

export function getLicensePrice(addon: any, currency: string): IPriceTier[] {
  if (!addon || !currency) {
    return [];
  }

  if (addon.sp.tier_type === "flat") {
    const amount =
      addon.sp.unit_cost.find((cost: any) => {
        return cost.currency === currency;
      })?.amount || 0;
    const licenseType = getAddonType(addon.sp.sp_sku_code);
    const tier = {
      startQuantity: 1,
      endQuantity: 999999999,
      amount: amount,
    };
    return [
      {
        ...tier,
        translationKey:
          licenseType === "server"
            ? "perAdditionalFinalTieredServerLicense"
            : "perAdditionalFinalTieredUserLicense",
        translationParams: tier,
      },
    ];
  }

  const tiers = addon.sp.tiered_cost
    .map((tier: any) => {
      const unitCost = tier.unit_cost.find((cost: any) => {
        return cost.currency === currency;
      });
      return {
        startQuantity: tier.starting_quantity,
        endQuantity: tier.ending_quantity,
        amount: unitCost?.amount || 0,
      };
    })
    .sort((a: any, b: any) => {
      return a.startQuantity - b.startQuantity;
    });

  const licenseType = getAddonType(addon.sp.sp_sku_code);
  const isSingleTier = tiers.length === 1;

  return tiers.map((tier: IPriceTier, index: number) => {
    return {
      ...tier,
      translationKey:
        licenseType === "server" && isSingleTier
          ? "perAdditionalFinalTieredServerLicense"
          : licenseType === "user" && index === tiers.length - 1
          ? "perAdditionalFinalTieredUserLicense"
          : "perAdditionalTieredUserLicense",
      translationParams: tier,
    };
  });
}

export function calculateLicensesPrice(
  priceTiers: IPriceTier[],
  quantity: number,
  baseLicenses = 0,
): number {
  const count = quantity - baseLicenses;
  if (count <= 0) {
    return 0;
  }

  let totalPrice = 0;
  let remainingLicenses = count;
  let currentPosition = 0;

  for (const tier of priceTiers) {
    if (currentPosition >= tier.endQuantity) {
      continue;
    }

    const tierStart = Math.max(tier.startQuantity - 1, currentPosition);
    const tierEnd = Math.min(
      tier.endQuantity,
      currentPosition + remainingLicenses,
    );
    const licensesInTier = tierEnd - tierStart;

    if (licensesInTier > 0) {
      totalPrice += licensesInTier * tier.amount;
      remainingLicenses -= licensesInTier;
      currentPosition = tierEnd;

      if (remainingLicenses <= 0) {
        break;
      }
    }
  }

  return totalPrice;
}

export function calculateCurrentPlanPrice(
  subscription: ICurrentSubscriptionDetails,
  userLicensePriceStructure: IPriceTier[],
  serverLicensePriceStructure: IPriceTier[],
  subscriptionWithProduct?: ISubscription,
): IMoney | null {
  if (!subscription?.sp.next_invoice_amount?.currency) {
    return null;
  }

  const currency = subscription.sp.next_invoice_amount.currency;
  const baseLicenses = subscription.licenses || 0;

  // Get user licenses price
  const userLicenseCount =
    (subscription.sp.addons?.find((addon) => {
      return getAddonType(addon.sp_sku_code) === "user";
    })?.quantity || 0) + baseLicenses;

  const userLicensesPrice = calculateLicensesPrice(
    userLicensePriceStructure,
    userLicenseCount,
    baseLicenses,
  );

  // Get server licenses price
  const serverLicenseCount =
    subscription.sp.addons?.find((addon) => {
      return getAddonType(addon.sp_sku_code) === "server";
    })?.quantity || 0;

  // Get base server licenses from VPN-SERVER entitlements
  const baseServerLicenses = getVpnServerEntitlementValue(
    subscriptionWithProduct?.sku?.product?.entitlements || [],
  );

  const serverLicensesPrice = calculateLicensesPrice(
    serverLicensePriceStructure,
    serverLicenseCount,
    baseServerLicenses,
  );

  // Get base subscription price
  const skuPrice =
    subscription.currentSKU?.sp.unit_cost.find((item: IMoney) => {
      return item.currency === currency;
    })?.amount || 0;

  return {
    amount: userLicensesPrice + serverLicensesPrice + skuPrice,
    currency,
  };
}

export function useBMRecurly() {
  try {
    return useRecurly();
  } catch (e) {
    return null;
  }
}

export const isTimeframeNow = (preview: ISubscriptionPreview) => {
  return preview.timeframe === NOW_TIMEFRAME;
};

export const calcCouponDiscount = ({
  details,
  estimate,
}: ISubscriptionPreview): number => {
  return details.discount?.amount || details.subtotal.amount - estimate.amount;
};

export const getVpnServerEntitlementValue = (
  entitlements: Array<{
    service_code: string;
    feature_code: string;
    value: number;
  }> = [],
): number => {
  const vpnServerEntitlement = entitlements.find((entitlement) => {
    return (
      entitlement.service_code === "VPN" &&
      (entitlement.feature_code === "SERVER_CONNECTIONS" ||
        entitlement.feature_code === "SERVER")
    );
  });

  return vpnServerEntitlement?.value || 0;
};
